import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { arrayHasItems, removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify'

export const OpenToBuyLoadingKeys = {
    getRelatedProducts: "GET_RELATED_PRODUCTS",
    getOpenToBuyProjects: "GET_OPEN_TO_BUY_PROJECTS",
    getOpenToBuyProjectsOptions: "GET_OTB_OPTIONS",
    upsertOpenToBuy: "UPSERT_OPEN_TO_BUY",
    updateSingleProduct: "UPDATE_SINGLE_PRODUCT",
    resetOverridesByProducts: "RESET_OVERRIDES_BY_PRODUCTS",
    deleteOpenToBuy: "DELETE_OPEN_TO_BUY"
}

const initialState = {
    loading: false,
    products: {
        otb: [],
        plan: [],
        otbTotals: {}
    },
    otbOptions: [],
    openToBuyProjects: [],
    loadingKeys: [],
    firstLoadComplete: false,
    error: null,
}

export const getOpenToBuyProjects = createAsyncThunk(
    'openToBuy/getOpenToBuyProjects',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/open-to-buy-projects');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getOpenToBuyProjectsOptions = createAsyncThunk(
    'openToBuy/getOpenToBuyProjectsOptions',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/options-otb');
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const getRelatedProducts = createAsyncThunk(
    'openToBuy/getRelatedProducts',
    async ({
        otbId,
        productGroupId,
        selectedForecast,
        selectedChannel,
        lookBack,
        year,
        startDate,
        endDate
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/related-products', {
                params: {
                    otbId,
                    productGroupId,
                    selectedForecast,
                    selectedChannel,
                    lookBack,
                    startDate,
                    year,
                    endDate
                }
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const updateSingleProduct = createAsyncThunk(
    'openToBuy/updateSingleProduct',
    async ({
        dataProduct
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/open-to-buy/single-update-product', { dataProduct });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
) 

export const resetOverrideMixByProducts = createAsyncThunk(
    'openToBuy/resetOverrideMixByProducts',
    async ({
        productIds,
        otbId
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/open-to-buy/reset-override-by-products', { productIds, otbId });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
) 

export const upsertOpenToBuyRequest = createAsyncThunk(
    'openToBuy/upsertOpenToBuyRequest',
    async ({
        openToBuyId,
        enabledActualBeginInventory,
        name,
        year,
        forecastId,
        plannedForecastId,
        options
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/open-to-buy/upsert', { 
                openToBuyId, 
                enabledActualBeginInventory,
                name, 
                year, 
                forecastId,
                plannedForecastId,
                options
            });
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const deleteOpenToBuyRequest = createAsyncThunk(
    'openToBuy/deleteOpenToBuyRequest',
    async ({
        id
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/open-to-buy/delete/${id}`);
            return data;
        } catch (error) {
            if ('string' === typeof error.response.data) {
                return rejectWithValue(error.response.data)
            } else {
                return rejectWithValue(error.response.data.errors || error.response.data.error)
            }
        }
    }
)

export const openToBuySlice = createSlice({
    name: 'openToBuy',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getOpenToBuyProjectsOptions.pending, (state) => {
                state.loading = true;
                state.firstLoadComplete = false;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.getOpenToBuyProjectsOptions];
            })
            .addCase(getOpenToBuyProjectsOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.otbOptions = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.getOpenToBuyProjectsOptions, state.loadingKeys);
            })
            .addCase(getOpenToBuyProjectsOptions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.otbOptions = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.getOpenToBuyProjectsOptions, state.loadingKeys);
            })
            .addCase(getOpenToBuyProjects.pending, (state) => {
                state.loading = true;
                state.firstLoadComplete = false;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.getOpenToBuyProjects];
            })
            .addCase(getOpenToBuyProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.openToBuyProjects = action.payload.items;
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.getOpenToBuyProjects, state.loadingKeys);
            })
            .addCase(getOpenToBuyProjects.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.openToBuyProjects = [];
                state.firstLoadComplete = true;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.getOpenToBuyProjects, state.loadingKeys);
            })
            .addCase(updateSingleProduct.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.updateSingleProduct];
            })
            .addCase(updateSingleProduct.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                const record = action.payload.updated;
                if (record) {
                    state.products = {
                        ...state.products,
                        otb: state.products.otb.map((product) =>
                            product.id === record.product_id
                                ? { ...product, overridenMix: Number(record.overriden_mix) }
                                : product
                        )
                    };
                }
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.updateSingleProduct, state.loadingKeys);
                toast.success(`Product updated successfully.`, { position: "bottom-right" })
            })
            .addCase(updateSingleProduct.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.updateSingleProduct, state.loadingKeys);
                toast.error(
                    `Failed to update product. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(upsertOpenToBuyRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.upsertOpenToBuy];
            })
            .addCase(upsertOpenToBuyRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.upsertOpenToBuy, state.loadingKeys);
                toast.success(`Open to buy updated successfully.`, { position: "bottom-right" })
            })
            .addCase(upsertOpenToBuyRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.upsertOpenToBuy, state.loadingKeys);
                toast.error(
                    `Failed to update open to buy. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(resetOverrideMixByProducts.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.resetOverridesByProducts];
            })
            .addCase(resetOverrideMixByProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                const productIds = action.payload?.productIds || [];
                if (arrayHasItems(productIds)) {
                    state.products = {
                        ...state.products,
                        otb: state.products.otb.map((product) =>
                            productIds.includes(product.id)
                                ? { ...product, overridenMix: 0 }
                                : product
                        ),
                    };
                }
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.resetOverridesByProducts, state.loadingKeys);
                toast.success(`Override mix reset successfully.`, { position: "bottom-right" })
            })
            .addCase(resetOverrideMixByProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.resetOverridesByProducts, state.loadingKeys);
                toast.error(
                    `Override mix can't be reset. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(deleteOpenToBuyRequest.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.deleteOpenToBuy];
            })
            .addCase(deleteOpenToBuyRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.deleteOpenToBuy, state.loadingKeys);
                toast.success(`Open to buy deleted successfully.`, { position: "bottom-right" })
            })
            .addCase(deleteOpenToBuyRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.deleteOpenToBuy, state.loadingKeys);
                toast.error(
                    `Open to buy can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
                )
            })
            .addCase(getRelatedProducts.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, OpenToBuyLoadingKeys.getRelatedProducts];
            })
            .addCase(getRelatedProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.products = action.payload.items;
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.getRelatedProducts, state.loadingKeys);
            })
            .addCase(getRelatedProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.products = [];
                state.loadingKeys = removeLoadingKey(OpenToBuyLoadingKeys.getRelatedProducts, state.loadingKeys);
            })
    }
})
export const {} = openToBuySlice.actions;
export default openToBuySlice.reducer 